import {
  Category,
  CategoryClassificationStructureAssignment,
} from "../models/categories";
import { AssetsResponse, ProjectId } from "../types";
import { Endpoints } from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

/**
 * Pure fetch / request-body-building logic for the classification-structure domain
 * (assets-api-server v3, ADASSETS-1405) — assigning `acc-classifications` trees to an asset
 * category. Deliberately React-free and dependency-light, mirroring `systemsClient.ts`; the
 * React-Query hooks that consume this live in `apps/acs-assets/src/clients/
 * classificationStructuresAPI.ts`.
 *
 * `categoryId` on all three endpoints is `category.internalId` (BIMOS numeric string), not the
 * v3 UUID `id`, despite the v3-shaped URL — confirmed from `categoryIdPathParam`'s own OpenAPI
 * schema example (`'123'`, not a UUID) and the manager class's own doc comment.
 */
export const getClassificationStructureClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => ({
  batchAssignClassificationStructures: (
    categoryInternalId: Category["internalId"],
    classificationTreeIds: string[]
  ): Promise<AssetsResponse<CategoryClassificationStructureAssignment[]>> =>
    http.post<AssetsResponse<CategoryClassificationStructureAssignment[]>>(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.Categories
      }/${categoryInternalId}/${
        Endpoints.ClassificationStructures
      }:batch-assign`,
      {
        body: JSON.stringify({
          data: classificationTreeIds.map((classificationTreeId) => ({
            classificationTreeId,
          })),
        }),
      }
    ),

  getClassificationStructuresForCategory: async (
    categoryInternalId: Category["internalId"],
    includeInherited: boolean
  ): Promise<CategoryClassificationStructureAssignment[]> => {
    const searchString = buildSearchString({ includeInherited });

    return (
      await http.get<
        AssetsResponse<CategoryClassificationStructureAssignment[]>
      >(
        `${http.getAssetsServiceBasePath(projectId, "v3")}/${
          Endpoints.Categories
        }/${categoryInternalId}/${
          Endpoints.ClassificationStructures
        }${searchString ? `?${searchString}` : ""}`
      )
    ).results;
  },

  // Returns 204 with no body.
  unassignClassificationStructure: (
    categoryInternalId: Category["internalId"],
    classificationTreeId: string
  ): Promise<void> =>
    http.del<void>(
      `${http.getAssetsServiceBasePath(projectId, "v3")}/${
        Endpoints.Categories
      }/${categoryInternalId}/${
        Endpoints.ClassificationStructures
      }/${classificationTreeId}`
    ),
});
